import { useTranslation } from "react-i18next";
import { formatStock } from "@/lib/formatStock";
import { usePestTranslation } from "./usePestTranslation";
import { useAuth } from "@/contexts/AuthContext";
import type { Notification } from "./useNotifications";
import { autoTitleTypeKey, translateTaskTitle } from "@/lib/taskTitle";

interface TranslatedNotification {
  title: string;
  message: string;
}

/**
 * Build the full agricultural hierarchy string LOCATION-FIRST: Localización · Nave · Bloque.
 * Skips empty parts so legacy notifications without `location_name` still render.
 */
function formatHierarchy(data: Record<string, unknown> | null | undefined): string {
  if (!data) return "";
  const location = (data.location_name as string) || "";
  const greenhouse = (data.greenhouse_name as string) || "";
  const block = (data.block_name as string) || "";
  return [location, greenhouse, block].filter(Boolean).join(" · ");
}

/**
 * Build hierarchy LOCATION-FIRST from shared location/greenhouse + a list of block names.
 */
function formatBatchHierarchy(
  blockNames: string[] | undefined,
  data: Record<string, unknown> | null | undefined
): string {
  const location = (data?.location_name as string) || "";
  const greenhouse = (data?.greenhouse_name as string) || "";
  const blocks = blockNames && blockNames.length > 0 ? blockNames.join(", ") : "";
  return [location, greenhouse, blocks].filter(Boolean).join(" · ");
}

export function useNotificationTranslation() {
  const { t, i18n } = useTranslation();
  const { getName: getPestName } = usePestTranslation();
  const { role } = useAuth();

  // Workers get a deliberately minimal layout: task TYPE + date, no location or
  // treatment name (they open the task for those). Other roles keep full detail.
  const isWorker = role === "worker";
  const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const taskTypeLabel = (taskType: string) =>
    capitalize(t(`notifications.messages.taskTypes.${taskType}`, taskType || ""));
  const fmtIntl = (iso: string | undefined, opts: Intl.DateTimeFormatOptions) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    try { return new Intl.DateTimeFormat(i18n.language, opts).format(d); } catch { return ""; }
  };
  const fmtDateTime = (iso?: string) => fmtIntl(iso, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const fmtDate = (iso?: string) => fmtIntl(iso, { day: "numeric", month: "short" });
  const fmtNumber = (n: number) => {
    if (!Number.isFinite(n)) return "";
    try { return new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(n); } catch { return String(n); }
  };
  const fmtDue = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOf(d) - startOf(new Date())) / 86400000);
    if (days <= 0) return t("notifications.messages.worker.dueToday");
    if (days === 1) return t("notifications.messages.worker.dueTomorrow");
    return t("notifications.messages.worker.dueInDays", { count: days });
  };
  const workerLine = (taskType: string, detail: string) =>
    [taskTypeLabel(taskType), detail].filter(Boolean).join(" · ");

  /**
   * CÓMO SE NOMBRA LA TAREA EN EL AVISO (02-09-2026).
   *
   * Un título autogenerado ya lleva la ubicación dentro —«Tratamiento — Las
   * Norias · Nave 1»— y las plantillas la vuelven a poner detrás, así que el
   * aviso salía «Tratamiento — Las Norias · Nave 1 in Las Norias · Nave 1 · A,
   * for Moustafa»: la finca dos veces y el mensaje ilegible.
   *
   * Si el título lo escribió una máquina no se enseña: se enseña el TIPO de
   * tarea en el idioma de quien lee («Tratamiento», «Mantenimiento»,
   * «Inspección»), y la ubicación y el asignado los pone la plantilla, una sola
   * vez. Un título escrito por una persona se respeta entero: dice algo que el
   * tipo no dice.
   *
   * Si no hay título, manda el tipo que venga en los datos. Y si el tipo tampoco
   * se reconoce, se devuelve el título tal cual: perder información es peor que
   * repetirla.
   */
  const nombreDeTarea = (title: string, taskType: string) => {
    const auto = autoTitleTypeKey(title);
    if (auto) return t(`planner.types.${auto}`, auto);
    if (title) return translateTaskTitle(title, t);
    return taskType ? t(`planner.types.${taskType}`, taskTypeLabel(taskType)) : "";
  };

  const translateNotification = (notification: Notification): TranslatedNotification => {
    const data = notification.data as Record<string, unknown> | null;
    const type = notification.type;

    // Worker-specific minimal layout for the task notification types.
    if (isWorker) {
      const taskType = (data?.task_type as string) || "";
      const sched = data?.scheduled_date as string | undefined;
      const fallback = notification.message || "";
      switch (type) {
        case "task_assigned":
          return { title: t("notifications.messages.worker.assignedTitle"), message: workerLine(taskType, fmtDateTime(sched)) || fallback };
        case "task_updated":
          return { title: t("notifications.messages.worker.updatedTitle"), message: workerLine(taskType, fmtDateTime(sched)) || fallback };
        case "task_due_soon":
          return { title: t("notifications.messages.worker.dueSoonTitle"), message: workerLine(taskType, fmtDue(sched)) || fallback };
        case "task_completed":
          return { title: t("notifications.messages.worker.completedTitle"), message: workerLine(taskType, fmtDate(sched)) || fallback };
        case "task_cancelled":
          return { title: t("notifications.messages.worker.cancelledTitle"), message: workerLine(taskType, fmtDate(sched)) || fallback };
        default:
          break;
      }
    }

    switch (type) {
      case 'task_created': {
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', (data?.task_type as string) || '');
        const taskType = (data?.task_type as string) || '';
        const location = formatHierarchy(data);
        const assignee = (data?.assigned_to_name as string) || '';

        const message = location
          ? (assignee
              ? t('notifications.messages.task_created.message', { task: taskTitle, location, assignee })
              : t('notifications.messages.task_created.messageNoAssignee', { task: taskTitle, location }))
          : notification.message || '';

        // Visita / inspección: título propio. Antes se pedía
        // `notifications.messages.visit_created.title`, una clave que no existe (esos
        // tipos se retiraron el 02-09) y el admin leía la clave cruda (M18/PREP-05).
        if (taskType === 'visit' || taskType === 'inspection') {
          return {
            title: t(`notifications.messages.task_created.${taskType === 'visit' ? 'titleVisit' : 'titleInspection'}`),
            message,
          };
        }

        const translatedType = taskType
          ? t(`notifications.messages.taskTypes.${taskType}`, taskType)
          : '';

        return {
          title: translatedType
            ? t('notifications.messages.task_created.title', { taskType: translatedType })
            : t('notifications.messages.task_created.titleSimple'),
          message,
        };
      }

      case 'task_assigned':
      case 'task_updated': {
        const taskType = (data?.task_type as string) || '';
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', taskType);
        const location = formatHierarchy(data);
        // T04-08: el aviso dice CUÁNDO es y, si se cambió, QUÉ cambió; y una visita o
        // una inspección se llaman así en el título.
        const when = fmtDateTime(data?.scheduled_date as string | undefined);
        const cambios = Array.isArray(data?.changes)
          ? (data!.changes as string[]).map((c) => t(`notifications.changes.${c}`, c)).join(', ')
          : '';
        const tituloDeVisita = taskType === 'visit' || taskType === 'inspection'
          ? t(`notifications.messages.${type}.${taskType === 'visit' ? 'titleVisit' : 'titleInspection'}`)
          : '';
        const body = (taskTitle || location)
          ? (when
              ? [taskTitle, when, location].filter(Boolean).join(' · ')
              : t(`notifications.messages.${type}.message`, { task: taskTitle, location }))
          : notification.message || '';
        return {
          title: tituloDeVisita || t(`notifications.messages.${type}.title`),
          message: cambios ? `${body} · ${t('notifications.changes.label', { changes: cambios })}` : body,
        };
      }

      case 'task_completed': {
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', (data?.task_type as string) || '');
        const taskType = (data?.task_type as string) || '';
        const completedBy = (data?.completed_by_name as string) || '';
        const treatmentName = (data?.treatment_name as string) || '';
        const rawPestName = (data?.pest_name as string) || '';
        const pestTranslationKey = (data?.pest_translation_key as string) || '';
        const pestName = pestTranslationKey
          ? getPestName({ name: rawPestName, translation_key: pestTranslationKey })
          : rawPestName;
        const location = formatHierarchy(data);
        // Preserve treatment/pest detail in the task label so it isn't lost.
        const taskLabel = [treatmentName || taskTitle, pestName].filter(Boolean).join(', ');

        // M18/PREP-05: antes `visit_completed.title`, una clave que no existe.
        const titleKey =
          taskType === 'visit' || taskType === 'inspection'
            ? `notifications.messages.task_completed.${taskType === 'visit' ? 'titleVisit' : 'titleInspection'}`
            : 'notifications.messages.task_completed.title';

        return {
          title: t(titleKey),
          message: (taskLabel || location)
            ? t('notifications.messages.task_completed.message', { task: taskLabel, location, user: completedBy })
            : notification.message || '',
        };
      }

      case 'task_cancelled': {
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', (data?.task_type as string) || '');
        const location = formatHierarchy(data);
        // T04-08 / M20: quién la canceló o la BORRÓ.
        const by = (data?.completed_by_name as string) || '';
        const base = (taskTitle || location)
          ? t('notifications.messages.task_cancelled.message', { task: taskTitle, location })
          : notification.message || '';

        return {
          title: t(data?.deleted ? 'notifications.messages.task_cancelled.titleDeleted' : 'notifications.messages.task_cancelled.title'),
          message: by && base ? t('notifications.messages.task_cancelled.messageBy', { base, user: by }) : base,
        };
      }

      case 'task_started':
      case 'task_stopped': {
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', (data?.task_type as string) || '');
        const actor = (data?.completed_by_name as string) || (data?.assigned_to_name as string) || '';
        const location = formatHierarchy(data);
        const messageKey = type === 'task_started'
          ? 'notifications.messages.task_started.message'
          : 'notifications.messages.task_stopped.message';
        const titleKey = type === 'task_started'
          ? 'notifications.messages.task_started.title'
          : 'notifications.messages.task_stopped.title';

        return {
          title: t(titleKey),
          message: (taskTitle || location)
            ? t(messageKey, { task: taskTitle, location, user: actor })
            : notification.message || '',
        };
      }

      // La revisión pedida (D17): pegado a la hora planeada y diciendo qué falta.
      case 'followup_not_started':
      case 'followup_unfinished': {
        const pest = ((data?.pest_name as string) || '').toLocaleUpperCase();
        const place = [data?.greenhouse_name, data?.block_name, data?.line_number != null ? `L${data.line_number}` : null].filter(Boolean).join(' · ');
        const missing = (data?.missing as string) || 'finish';
        const missingText =
          missing === 'verdict' ? t('worker.review.missingVerdict') : missing === 'media' ? t('worker.review.missingMedia') : t('worker.review.missingFinish');
        return {
          title: t(`notifications.messages.${type}.title`),
          message: pest || place
            ? t(`notifications.messages.${type}.message`, { pest, place, missing: missingText })
            : notification.message || '',
        };
      }

      case 'task_due_soon': {
        const taskType = (data?.task_type as string) || '';
        const taskTitle = nombreDeTarea((data?.task_title as string) || '', taskType);
        const date = fmtDateTime(data?.scheduled_date as string | undefined) || (data?.scheduled_date as string) || '';
        const location = formatHierarchy(data);
        // M21: recordatorio de la visita de mañana y de la atrasada (perito_visit_reminders).
        const kind = data?.kind as string | undefined;
        const title = kind === 'overdue'
          ? t('notifications.messages.task_due_soon.titleOverdue')
          : kind === 'tomorrow'
            ? t('notifications.messages.task_due_soon.titleTomorrow')
            : t('notifications.messages.task_due_soon.title');

        return {
          title,
          message: taskTitle
            ? [t('notifications.messages.task_due_soon.message', { task: taskTitle, date }), location].filter(Boolean).join(' · ')
            : notification.message || '',
        };
      }

      case 'low_stock': {
        const productName = (data?.product_name as string) || '';
        const currentStock = data?.current_stock as number;

        return {
          title: t('notifications.messages.low_stock.title'),
          message: productName
            ? t('notifications.messages.low_stock.message', { product: productName, stock: formatStock(currentStock ?? 0) })
            : notification.message || '',
        };
      }

      case 'team_member_added': {
        const memberName = (data?.full_name as string) || (data?.member_name as string) || '';

        /* Un perito que se une por invitación (tanda 5, A10): mismo tipo de aviso,
           con su propio texto. */
        const esPerito = data?.role === 'perito';
        return {
          title: t('notifications.messages.team_member_added.title'),
          message: memberName
            ? t(
                esPerito
                  ? 'notifications.messages.team_member_added.peritoMessage'
                  : 'notifications.messages.team_member_added.message',
                { name: memberName },
              )
            : notification.message || '',
        };
      }

      case 'treatment_logged': {
        const location = formatHierarchy(data);
        const productCount = data?.product_count as number;
        // M21: con los productos y su dosis (en el formato del idioma) y hasta cuándo
        // no se puede cosechar, si la función del servidor lo manda.
        const lines = Array.isArray(data?.product_lines)
          ? (data!.product_lines as Array<{ name?: string; dose?: number | null; unit?: string }>)
          : [];
        const products = lines.length
          ? lines
              .map((l) => [l.name, l.dose != null ? `${fmtNumber(l.dose)} ${l.unit || ''}`.trim() : ''].filter(Boolean).join(' '))
              .join(', ')
          : '';
        const phiEnd = data?.phi_end ? fmtIntl(`${data.phi_end as string}T12:00:00`, { day: 'numeric', month: 'short' }) : '';
        const base = location
          ? (products
              ? t('notifications.messages.treatment_logged.messageProducts', { location, products })
              : t('notifications.messages.treatment_logged.message', { location, count: productCount ?? 1 }))
          : notification.message || '';

        return {
          title: t('notifications.messages.treatment_logged.title'),
          message: phiEnd ? `${base} · ${t('notifications.messages.treatment_logged.phiUntil', { date: phiEnd })}` : base,
        };
      }

      case 'irm_warning': {
        const productName = (data?.product_name as string) || '';
        const location = formatHierarchy(data);
        const irmStatus = (data?.irm_status as string) || 'warning';
        const appsUsed = data?.applications_used as number;
        const maxApps = data?.max_applications as number;

        if (irmStatus === 'over') {
          return {
            title: t('notifications.messages.irm_warning.title_exceeded', { product: productName }),
            message: productName
              ? t('notifications.messages.irm_warning.message_exceeded', { product: productName, location, used: appsUsed ?? '?', max: maxApps ?? '?' })
              : notification.message || '',
          };
        }

        return {
          title: t('notifications.messages.irm_warning.title_attention', { product: productName }),
          message: productName
            ? t('notifications.messages.irm_warning.message_attention', { product: productName, location, used: appsUsed ?? '?', max: maxApps ?? '?' })
            : notification.message || '',
        };
      }

      case 'product_request': {
        const requesterName = (data?.requester_name as string) || '';
        const productName = (data?.product_name as string) || notification.message || '';
        const location = formatHierarchy(data);
        /* La cantidad en el formato del idioma de quien lee: «0,1 kg», no «0.1 kg». */
        const rawQuantity = data?.quantity as number | string | undefined;
        const quantity = rawQuantity === undefined || rawQuantity === '' || !Number.isFinite(Number(rawQuantity))
          ? (rawQuantity ?? '')
          : fmtNumber(Number(rawQuantity));
        const unit = (data?.unit as string) || '';

        return {
          title: t('notifications.messages.product_request.title'),
          message: requesterName
            ? t(
                location
                  ? 'notifications.messages.product_request.messageDetailed'
                  : 'notifications.messages.product_request.messageDetailedNoLocation',
                { name: requesterName, product: productName, location, quantity, unit },
              )
            : notification.message || '',
        };
      }

      // M7: a quien pidió un producto, que se lo atendieron o se lo descartaron.
      case 'product_request_resolved': {
        const productName = (data?.product_name as string) || notification.message || '';
        const quantity = data?.quantity as number | string | undefined;
        const unit = (data?.unit as string) || '';
        const name = (data?.resolver_name as string) || '';
        const qty = quantity != null && quantity !== '' ? `${fmtNumber(Number(quantity))} ${unit}`.trim() : '';
        const what = [qty, productName].filter(Boolean).join(' · ');
        const dismissed = data?.status === 'dismissed';
        return {
          title: t(dismissed
            ? 'notifications.messages.product_request_resolved.titleDismissed'
            : 'notifications.messages.product_request_resolved.title'),
          message: name ? t('notifications.messages.product_request_resolved.messageBy', { what, name }) : what,
        };
      }

      // M17: foco nuevo grave (hoy solo al perito).
      case 'pest_focus_new': {
        const rawPestName = (data?.pest_name as string) || '';
        const pestKey = (data?.pest_translation_key as string) || '';
        const pest = pestKey ? getPestName({ name: rawPestName, translation_key: pestKey }) : rawPestName;
        const location = [formatHierarchy(data), data?.line_number != null ? `L${data.line_number}` : '']
          .filter(Boolean).join(' · ');
        const sev = data?.severity === 'critical'
          ? t('notifications.messages.pest_focus_new.severityCritical')
          : t('notifications.messages.pest_focus_new.severityHigh');
        return {
          title: pest
            ? t('notifications.messages.pest_focus_new.title', { pest })
            : t('notifications.messages.pest_focus_new.titleSimple'),
          message: [location, sev].filter(Boolean).join(' · ') || notification.message || '',
        };
      }

      // P5: un foco abierto empeora (un parte grave entra o sube la gravedad). Solo al perito.
      case 'pest_focus_worse': {
        const rawPestName = (data?.pest_name as string) || '';
        const pestKey = (data?.pest_translation_key as string) || '';
        const pest = pestKey ? getPestName({ name: rawPestName, translation_key: pestKey }) : rawPestName;
        const location = [formatHierarchy(data), data?.line_number != null ? `L${data.line_number}` : '']
          .filter(Boolean).join(' · ');
        const sev = data?.severity === 'critical'
          ? t('notifications.messages.pest_focus_worse.severityCritical')
          : t('notifications.messages.pest_focus_worse.severityHigh');
        return {
          title: pest
            ? t('notifications.messages.pest_focus_worse.title', { pest })
            : t('notifications.messages.pest_focus_worse.titleSimple'),
          message: [location, sev].filter(Boolean).join(' · ') || notification.message || '',
        };
      }

      case 'worker_quick_report': {
        const workerName = (data?.worker_name as string) || '';
        const location = formatHierarchy(data);
        const hasPhoto = !!data?.has_photo;
        const hasAudio = !!data?.has_audio;

        const media = hasPhoto && hasAudio
          ? t('notifications.messages.worker_quick_report.photoAudio')
          : hasPhoto
            ? t('notifications.messages.worker_quick_report.photo')
            : hasAudio
              ? t('notifications.messages.worker_quick_report.audio')
              : '';

        const body = location
          ? t('notifications.messages.worker_quick_report.message', { location })
          : '';
        const message = body
          ? (media ? `${body} · ${media}` : body)
          : notification.message || '';

        return {
          title: workerName
            ? t('notifications.messages.worker_quick_report.title', { user: workerName })
            : t('notifications.messages.worker_quick_report.titleSimple'),
          message,
        };
      }

      default:
        // Fallback for unknown/legacy types
        return {
          title: notification.title || t('notifications.types.' + type, type || ''),
          message: notification.message || '',
        };
    }
  };

  return { translateNotification };
}
